import * as React from 'react'
import { Loader2, RefreshCw, ServerCog, Plug } from 'lucide-react'
import {
  createOntologyHeaders,
  getOntologyApiBaseUrl,
  useRunCypherMutation,
  type GraphCypherQueryResult
} from '@axilon-apps/domains-ontology-ui-state'
import { Button } from '../../../components/ui/button'
import { Card, CardContent } from '../../../components/ui/card'
import { StatusPill } from '../../../components/primitives/StatusPill'
import type { Status } from '../../../components/primitives/types'

type CheckState = 'idle' | 'loading' | 'ok' | 'error'

type TargetKind = 'gateway' | 'opc'

interface ConnectionTarget {
  /** Stable id, derived from the gateway node id + kind. */
  id: string
  /** The URL the proxy will probe. */
  url: string
  /** Whether this is the Ignition gateway URL or the OPC UA endpoint. */
  kind: TargetKind
  /** Human label describing what the URL is for (e.g. "Plant A · Gateway URL"). */
  label: string
  state: CheckState
  message?: string
}

const KIND_LABELS: Record<TargetKind, string> = {
  gateway: 'Gateway URL',
  opc: 'OPC UA endpoint'
}

const STATUS_PILL: Record<CheckState, { status: Status; label: string }> = {
  idle: { status: 'idle', label: 'pending' },
  loading: { status: 'info', label: 'checking…' },
  ok: { status: 'run', label: 'reachable' },
  error: { status: 'down', label: 'unreachable' }
}

const FETCH_TIMEOUT_MS = 8000

const GATEWAY_QUERY = `
  MATCH (g:Gateway)
  RETURN g.id AS id,
         coalesce(g.name, g.id) AS name,
         g.gateway_url AS gateway_url,
         g.opc_endpoint AS opc_endpoint,
         g.gateway_type AS gateway_type
  ORDER BY toLower(coalesce(g.name, g.id))
`

function readString(row: Record<string, unknown>, key: string): string | undefined {
  const value = row[key]
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

function buildTargets(rows: Array<Record<string, unknown>>): ConnectionTarget[] {
  const out: ConnectionTarget[] = []
  for (const row of rows) {
    const id = readString(row, 'id') ?? ''
    const name = readString(row, 'name') ?? id ?? 'Unnamed gateway'
    const gatewayUrl = readString(row, 'gateway_url')
    const opcEndpoint = readString(row, 'opc_endpoint')

    if (gatewayUrl) {
      out.push({
        id: `${id || gatewayUrl}::gateway`,
        url: gatewayUrl,
        kind: 'gateway',
        label: name,
        state: 'idle'
      })
    }
    if (opcEndpoint) {
      out.push({
        id: `${id || opcEndpoint}::opc`,
        url: opcEndpoint,
        kind: 'opc',
        label: name,
        state: 'idle'
      })
    }
  }
  return out
}

function buildProbeUrl(base: string, target: ConnectionTarget): string {
  const params = new URLSearchParams()
  if (target.kind === 'gateway') {
    params.set('gateway_url', target.url)
    return `${base}/live-data/gateway-status?${params.toString()}`
  }
  params.set('endpoint', target.url)
  return `${base}/opc/status?${params.toString()}`
}

function extractMessage(body: unknown, fallback: string): string {
  if (body && typeof body === 'object' && !Array.isArray(body)) {
    const record = body as Record<string, unknown>
    const candidates = ['error', 'message', 'detail']
    for (const key of candidates) {
      const value = record[key]
      if (typeof value === 'string' && value.trim()) return value.trim()
    }
  }
  if (typeof body === 'string' && body.trim()) return body.trim()
  return fallback
}

async function probeTarget(
  base: string,
  target: ConnectionTarget,
  signal: AbortSignal
): Promise<{ ok: boolean; message?: string }> {
  const url = buildProbeUrl(base, target)
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: createOntologyHeaders(),
      signal
    })
    const text = await response.text().catch(() => '')
    let parsed: unknown = null
    if (text.trim()) {
      try {
        parsed = JSON.parse(text)
      } catch {
        parsed = text
      }
    }

    if (!response.ok) {
      return {
        ok: false,
        message: extractMessage(parsed, `HTTP ${response.status}`)
      }
    }

    return { ok: true }
  } catch (err) {
    if ((err as Error).name === 'AbortError') {
      return { ok: false, message: 'request timed out' }
    }
    return { ok: false, message: (err as Error).message || 'network error' }
  }
}

export function ConnectionsCheckCard() {
  const [runCypher, runState] = useRunCypherMutation()
  const [targets, setTargets] = React.useState<ConnectionTarget[]>([])
  const [loadError, setLoadError] = React.useState('')
  const [isChecking, setIsChecking] = React.useState(false)
  const [hasLoaded, setHasLoaded] = React.useState(false)
  const abortRef = React.useRef<AbortController | null>(null)

  const updateTarget = React.useCallback(
    (id: string, patch: Partial<ConnectionTarget>) => {
      setTargets((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)))
    },
    [setTargets]
  )

  const runChecks = React.useCallback(
    async (items: ConnectionTarget[]) => {
      if (items.length === 0) return
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller

      setIsChecking(true)
      const base = getOntologyApiBaseUrl()
      try {
        await Promise.all(
          items.map(async (target) => {
            updateTarget(target.id, { state: 'loading', message: undefined })
            const timeout = window.setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)
            try {
              const result = await probeTarget(base, target, controller.signal)
              updateTarget(target.id, {
                state: result.ok ? 'ok' : 'error',
                message: result.message
              })
            } finally {
              window.clearTimeout(timeout)
            }
          })
        )
      } finally {
        setIsChecking(false)
      }
    },
    [updateTarget]
  )

  const handleRefresh = React.useCallback(async () => {
    setLoadError('')
    try {
      const result = (await runCypher({
        query: GATEWAY_QUERY
      }).unwrap()) as GraphCypherQueryResult
      const next = buildTargets(result.results ?? [])
      setTargets(next)
      setHasLoaded(true)
      void runChecks(next)
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load Gateway nodes.')
      setHasLoaded(true)
    }
  }, [runCypher, runChecks])

  React.useEffect(() => {
    void handleRefresh()
    return () => {
      abortRef.current?.abort()
    }
    // Run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isLoadingList = runState.isLoading && !hasLoaded
  const summary = React.useMemo(() => {
    if (targets.length === 0) return ''
    const ok = targets.filter((t) => t.state === 'ok').length
    const failed = targets.filter((t) => t.state === 'error').length
    const pending = targets.filter((t) => t.state === 'idle' || t.state === 'loading').length
    const parts: string[] = []
    if (ok) parts.push(`${ok} reachable`)
    if (failed) parts.push(`${failed} failing`)
    if (pending) parts.push(`${pending} pending`)
    return parts.join(' · ')
  }, [targets])

  return (
    <Card className="gap-3 py-4">
      <CardContent className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-col gap-0.5">
            <h3 className="text-sm font-semibold text-ink">Gateway connections</h3>
            <span className="text-xs text-ink-3">
              Probes every Gateway URL and OPC UA endpoint stored in the ontology to verify the
              proxy server can reach them.
            </span>
          </div>
          <Button
            size="xs"
            variant="outline"
            onClick={() => void handleRefresh()}
            disabled={runState.isLoading || isChecking}
          >
            {runState.isLoading || isChecking ? (
              <Loader2 className="animate-spin" />
            ) : (
              <RefreshCw />
            )}
            Recheck
          </Button>
        </div>

        {loadError ? (
          <div role="alert" className="rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down">
            {loadError}
          </div>
        ) : null}

        {isLoadingList ? (
          <div className="flex items-center gap-2 py-2.5 text-xs text-ink-3">
            <Loader2 className="size-3 animate-spin" />
            Loading Gateway nodes…
          </div>
        ) : null}

        {hasLoaded && targets.length === 0 && !loadError ? (
          <div className="rounded-md bg-st-idle-bg px-3 py-2 text-xs text-ink-3">
            No Gateway nodes with a <code className="font-mono">gateway_url</code> or{' '}
            <code className="font-mono">opc_endpoint</code> were found in the current ontology
            branch.
          </div>
        ) : null}

        {targets.length > 0 ? (
          <ul className="flex flex-col">
            {targets.map((target) => {
              const pill = STATUS_PILL[target.state]
              const Icon = target.kind === 'gateway' ? ServerCog : Plug
              return (
                <li
                  key={target.id}
                  className="flex items-center gap-3 border-b border-line py-2.5 last:border-b-0"
                >
                  <Icon className="size-3.5 shrink-0 text-ink-3" aria-hidden="true" />
                  <div className="flex min-w-0 flex-1 flex-col">
                    <div className="flex items-center gap-2 text-sm text-ink">
                      <span className="truncate font-medium">{target.label}</span>
                      <span className="rounded-sm bg-st-idle-bg px-1.5 py-0.5 text-10 font-medium tracking-[0.02em] text-ink-2 uppercase">
                        {KIND_LABELS[target.kind]}
                      </span>
                    </div>
                    <span className="truncate font-mono text-11 text-ink-3" title={target.url}>
                      {target.url}
                    </span>
                    {target.state === 'error' && target.message ? (
                      <span className="truncate text-11 text-st-down" title={target.message}>
                        {target.message}
                      </span>
                    ) : null}
                  </div>
                  <StatusPill status={pill.status} className="shrink-0">
                    {pill.label}
                  </StatusPill>
                </li>
              )
            })}
          </ul>
        ) : null}

        {summary ? <span className="text-11 text-ink-3">{summary}</span> : null}
      </CardContent>
    </Card>
  )
}
